import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Body2, Dot, IconButton, TrashIcon, Tooltip, MoveIcon, Select, SelectTrigger } from "@activecollab/components";
import PropTypes from "prop-types";
import { showErrorToast } from "~/components/Toast";
import { useUpdateApiToken } from "~/repository";
import { getPersonalizedDateFormat } from "~/store/selectors/selector.configOptions";
import { formatDateSmart } from "~/utils/date";

const ApiTokensItem = ({ item, setDeleteElement, setSelectedTokenId, userId }) => {
  const { t } = useTranslation();
  const format = useSelector(getPersonalizedDateFormat);
  const updateApiTokenMutation = useUpdateApiToken();

  const options = useMemo(
    () => [
      { id: false, name: t("Read Only") },
      { id: true, name: t("Read and Write") },
    ],
    [t]
  );

  const handleAccessChange = (value) => {
    updateApiTokenMutation.mutate(
      {
        userId,
        tokenId: item.id,
        data: { is_read_write: value },
      },
      {
        onError: () => {
          showErrorToast(t("Unable to change access level."));
        },
      }
    );
  };

  return (
    <>
      <div className="tw-flex tw-items-center tw-pl-5 tw-pr-4 tw-min-h-[48px] tw-gap-6 tw-border-0 tw-border-t tw-border-solid tw-border-theme-400">
        <Body2
          overflow="truncate"
          whitespace="no-wrap"
          className="tw-min-w-[250px] tw-shrink-0 tw-flex-1 tw-w-full"
        >
          {item.client_display_name}
        </Body2>
        <Body2
          color="tertiary"
          className="tw-w-[100px] tw-shrink-0"
        >
          {formatDateSmart(item.last_used_on, format)}
        </Body2>
        <Body2
          className="tw-flex tw-w-[140px] tw-shrink-0 tw-items-center tw-select-none tw-overflow-hidden"
          color="tertiary"
        >
          <>
            <span className="tw-flex tw-shrink-0">{item.token}</span>
            {[...Array(7)].map((_, index) => (
              <Dot
                data-testid="dot"
                key={`dot-${index}`}
                size={8}
                className="tw-ml-1 tw-mt-0.5 tw-shrink-0"
                color="var(--color-theme-600)"
              />
            ))}
          </>
        </Body2>
        <div className="tw-w-[32px] tw-shrink-0">
          <Tooltip title={t("Use Token")}>
            <IconButton
              variant="text gray"
              onClick={() => setSelectedTokenId(item.id)}
            >
              <MoveIcon />
            </IconButton>
          </Tooltip>
        </div>
        <div className="tw-w-[140px] tw-shrink-0">
          <Select
            options={options}
            selected={item.is_read_write}
            onChange={handleAccessChange}
            keepSameOptionsOrder
            disableSearch
            forceCloseMenu
            disabledInternalSort
            disableVirtualization
            target={
              <SelectTrigger
                mode="flat"
                className="tw-w-full tw-p-0"
              >
                {options.find((option) => option.id === item.is_read_write)?.name}
              </SelectTrigger>
            }
          />
        </div>
        <div className="tw-flex tw-gap-4">
          <Tooltip title={t("Delete Token")}>
            <IconButton
              className="tw-shrink-0"
              variant="text gray"
              onClick={() => setDeleteElement(item)}
            >
              <TrashIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </>
  );
};

ApiTokensItem.propTypes = {
  item: PropTypes.object,
  setDeleteElement: PropTypes.func,
  setSelectedTokenId: PropTypes.func,
  userId: PropTypes.number,
};

export default ApiTokensItem;
